import {
  type EngineState,
  type HintReveal,
  type ReviveView,
  formatSeed,
  gradeOf,
  summarize,
  toAnswerSlots,
} from '../engine';
import { t } from '../i18n';
import type { LenLedger, SealView, Stamp } from './seal';

export interface DomRefs {
  readonly band: HTMLElement;
  readonly question: HTMLElement;
  readonly slots: HTMLElement;
  readonly stamps: HTMLElement;
  readonly hud: HTMLElement;
  readonly overlay: HTMLElement;
  readonly toast: HTMLElement;
}

export function queryDom(): DomRefs {
  const get = (id: string): HTMLElement => {
    const el = document.getElementById(id);
    if (el === null) throw new Error(`DOM 요소 '${id}' 없음`);
    return el;
  };
  return {
    band: get('band'),
    question: get('question'),
    slots: get('slots'),
    stamps: get('stamps'),
    hud: get('hud'),
    overlay: get('overlay'),
    toast: get('toast'),
  };
}

/** 상단 띠 — 이미 먹은 글자만 채운다. 정답 문자열은 뷰로 나가지 않는다(ADR-008). */
export function renderBand(
  dom: DomRefs,
  s: EngineState,
  questionText: string,
  readyPip = 0,
  hint: HintReveal | null = null,
): void {
  // 추론형 연상 힌트는 문제문 뒤에 ` · ` 로 이어 붙는다(RULES §8.3). 타일은 손대지 않는다
  const qText =
    hint !== null && hint.assoc.length > 0
      ? `${questionText} · ${hint.assoc.join(' · ')}`
      : questionText;
  if (dom.question.textContent !== qText) {
    dom.question.textContent = qText;
    // 힌트가 붙어 길어지면 한 단계 작은 글자로 — 띠는 2줄까지만 잡혀 있다(UPLIFT §3.4c). 28자는 폰 폭(332px)에서 15px 2줄의 경계
    dom.question.classList.toggle('long', qText.length > 28);
  }
  const slots = toAnswerSlots(s);
  // 초성 힌트는 슬롯 `_` 자리에 들어간다 — 이미 먹은 글자가 우선
  const marks = slots.map((c, k) => (c !== null ? c : (hint?.cho[k] ?? '_'))).join(' ');
  // READY pip 3·2·1 은 필드 밖(밴드)에 둔다 — 필드 중앙 링·'출발!' 은 먼저 출발한 사람의 화면을 가린다(UPLIFT §6.4)
  const pip =
    readyPip > 0 ? `READY ${'●'.repeat(readyPip)}${'○'.repeat(Math.max(0, 3 - readyPip))} · ` : '';
  const text = `${pip}${t().band.answerLine(slots.length, marks)}`;
  if (dom.slots.textContent !== text) dom.slots.textContent = text;
}

const STAMP_CLASS: Record<Stamp, string> = {
  sealed: 'st sealed',
  failed: 'st failed',
  current: 'st cur',
  todo: 'st',
};
/** 도장 글리프도 형태로 중복 부호화한다 — 색을 못 봐도 채움/사선/테두리로 구분된다. */
const STAMP_GLYPH: Record<Stamp, string> = {
  sealed: '■',
  failed: '▨',
  current: '▶',
  todo: '□',
};

/**
 * 봉인 진행도 8칸. **압박 링(캔버스 테두리)과 절대 같은 표면에 두지 않는다** —
 * 링은 나를 조여오는 양(차오르면 나쁨), 도장은 내가 해낸 양(채워지면 좋음)이라
 * 같은 자리에 두면 방향이 정반대인 두 게이지가 한 눈에 섞인다.
 */
export function renderStamps(dom: DomRefs, v: SealView): void {
  const el = dom.stamps;
  if (el.childElementCount !== v.total) {
    el.innerHTML = '';
    for (let i = 0; i < v.total; i++) el.append(document.createElement('i'));
  }
  for (let i = 0; i < v.total; i++) {
    const st = v.stamps[i] as Stamp;
    const node = el.children[i] as HTMLElement;
    // className 으로 비교하면 자기가 붙인 'punch' 때문에 매 프레임 불일치 → 봉인 칸이 애니메이션
    // 첫 프레임(scale 2.2 · opacity 0.2)에 영구 고착된다. 상태는 dataset 에 따로 적고 그것만 비교한다.
    if (node.dataset.st !== st) {
      node.dataset.st = st;
      node.className = STAMP_CLASS[st];
      node.textContent = STAMP_GLYPH[st];
      // 봉인이 찍히는 순간에만 punch 애니메이션을 재기동한다
      if (st === 'sealed') {
        node.classList.remove('punch');
        void node.offsetWidth;
        node.classList.add('punch');
      }
    }
  }
}

export function formatClock(ms: number): string {
  const total = Math.floor(ms / 1000);
  const mm = Math.floor(total / 60);
  const ss = total % 60;
  return `${mm}:${ss < 10 ? '0' : ''}${ss}`;
}

let lastHud = '';
/**
 * HUD. 우승 기준 C 의 세 축(봉인 수 → 시간 → 도달 문제)만 싣는다.
 * `길이 L/MAX` 는 분모가 곧 임계값이라 "27 이 나쁜 값인가"를 따로 학습하지 않아도 된다.
 * 점수는 결과 패널로 내린다 — 순위 키가 아닌 값을 상시 표시하면 목표가 흐려진다.
 */
export function renderHud(dom: DomRefs, s: EngineState, v: SealView, right?: string | null): void {
  // 도전 판에서는 시드 대신 `목표 …`(상대 기록) — 최악 문자열이 폭을 넘지 않게 둘 중 하나만
  const tail = right != null ? right : formatSeed(s.seed);
  const text = t().hud.line(
    v.sealed,
    v.total,
    v.logicalLen,
    v.lenMax,
    formatClock(s.nominalMs),
    tail,
  );
  if (text !== lastHud) {
    lastHud = text;
    dom.hud.textContent = text;
  }
  const tier = v.tier === 2 ? 'crit' : v.tier === 1 ? 'warn' : '';
  if (dom.hud.dataset.tier !== tier) dom.hud.dataset.tier = tier;
}

export function showToast(dom: DomRefs, text: string, ms: number): void {
  dom.toast.classList.remove('intro');
  dom.toast.textContent = text;
  dom.toast.classList.add('on');
  window.setTimeout(() => dom.toast.classList.remove('on'), ms);
}

/**
 * 첫 판 인트로 — 규칙이 스네이크 관습을 **뒤집는다**(먹으면 짧아진다).
 * 이걸 말로 한 번 안 하면 첫 판 내내 "먹으면 길어진다"는 오해 위에서 판단하게 된다.
 * 수명은 벽시계가 아니라 **READY 틱**이다 — 호출부가 `readyTicksLeft === 0` 이 되는 프레임에 지운다
 * (첫 입력이 READY 를 건너뛰면 그 즉시). 벽시계 3.4초로 두면 플레이 시작 뒤 1.4초가 남아
 * 100% 의 판에서 머리·목을 가렸다(SEAL §5.E). 필드 하단(행 14~17)에 띄워 시작 위치와 겹치지 않는다.
 * `ms > 0` 이면 안전망으로 그 시간 뒤에도 지운다(루프가 멈춘 채 남지 않게).
 */
export function showIntro(dom: DomRefs, lines: readonly string[], ms = 0): () => void {
  dom.toast.innerHTML = '';
  for (const line of lines) {
    const p = document.createElement('span');
    p.textContent = line;
    dom.toast.append(p);
  }
  dom.toast.classList.add('on', 'intro');
  const hide = (): void => dom.toast.classList.remove('on', 'intro');
  if (ms > 0) window.setTimeout(hide, ms);
  return hide;
}

/**
 * 길이 원장 — 사망 화면의 핵심 한 줄. 항의 **순서를 고정**하고 크기순 정렬을 금지한다.
 * 정렬하면 "오답 +12" 가 맨 앞에 오는 판이 생기고, 그 화면은 플레이어에게
 * "오답 때문에 죽었다"로 읽힌다. 설계원칙 1(오답으로 판이 끝나지 않는다)을
 * 규칙이 지켜도 화면이 뒤집는 경로가 정확히 여기다.
 */
export function formatLedger(l: LenLedger, total: number): string {
  const sign = (n: number): string => (n >= 0 ? `+${n}` : `${n}`);
  return t().ledger.line(
    total,
    l.init,
    sign(l.byTime),
    sign(l.byAnswer),
    sign(l.byWord),
    sign(l.byWrong),
  );
}

export function showGameOver(
  dom: DomRefs,
  s: EngineState,
  v: SealView,
  onRestart: () => void,
): void {
  const cleared = v.sealed === v.total;
  const trapped = s.endReason === 'self';
  const S = t().over;
  const title = cleared
    ? S.sealed
    : s.endReason === 'wall'
      ? S.wall
      : trapped
        ? S.self
        : s.endReason === 'engorged'
          ? S.engorged
          : s.endReason === 'unsealed'
            ? S.unsealed
            : S.end;
  // 봉인 시간은 마지막 정답 틱까지다(SEAL RANK_KEY) — 그 뒤 WORD_CLEAR 정지 틱은 순위에 안 들어간다.
  // 명목 ms 는 틱의 상수배라 s.nominalMs / s.tick 이 곧 틱당 명목 ms 다.
  const sealMs = s.tick > 0 ? (s.lastSolveTick * s.nominalMs) / s.tick : 0;

  dom.overlay.innerHTML = '';
  const box = document.createElement('div');
  box.className = cleared ? 'panel win' : 'panel';
  const h = document.createElement('h1');
  h.textContent = title;
  const rank = document.createElement('p');
  rank.className = 'rank';
  // 우승 기준 C — 성공은 시간이, 실패는 도달 문제 수가 1차 순위다. 화면도 그 순서로 읽힌다.
  rank.textContent = cleared
    ? `${formatClock(sealMs)} · ${v.total}/${v.total}`
    : S.rank(v.sealed, v.total, formatClock(s.nominalMs));
  const led = document.createElement('p');
  led.className = 'ledger';
  led.textContent = formatLedger(v.ledger, v.logicalLen);
  const sub = document.createElement('p');
  // 등급은 순위 키만으로 계산된다(ADR-023). 최대 길이는 자랑 줄이 아니라 원장 안에서만 보인다.
  sub.textContent = S.grade(gradeOf(summarize(s, 0, v.total)));
  const btn = document.createElement('button');
  btn.id = 'restart';
  btn.textContent = cleared ? S.faster : S.again;
  btn.addEventListener('click', onRestart);
  box.append(h, rank, led, sub, btn);
  dom.overlay.append(box);
  dom.overlay.classList.add('on');
}

export function hideGameOver(dom: DomRefs): void {
  dom.overlay.classList.remove('on');
  dom.overlay.innerHTML = '';
}

/**
 * 부활 화면(RULES §1.14). 새 DOM 요소 0 — #overlay 한 장 안에 panel 로 그린다.
 * 정답 위치는 ReviveView 가 판정 뒤에만 준다(revealIndex). 네 버튼은 외형이 완전히 같다.
 * 오버레이가 문제 띠까지 덮으므로 진행 중 문제의 무료 사고시간이 되지 않는다.
 */
export function renderRevive(root: HTMLElement, v: ReviveView, onPick: (i: number) => void): void {
  const box = document.createElement('div');
  box.className = 'panel revive';
  const cause = document.createElement('p');
  cause.className = 'rv-cause';
  cause.textContent = v.cause === 'self' ? t().revive.self : t().revive.wall;
  const sub = document.createElement('p');
  sub.className = 'rv-sub';
  sub.textContent = t().revive.sub;
  const bar = document.createElement('div');
  bar.className = 'rv-bar';
  bar.append(document.createElement('i'));
  const q = document.createElement('p');
  q.className = 'rv-q';
  q.textContent = v.question;
  const grid = document.createElement('div');
  grid.className = 'rv-grid';
  v.choices.forEach((c, i) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = c;
    b.addEventListener('click', () => onPick(i));
    grid.append(b);
  });
  const verdict = document.createElement('p');
  verdict.className = 'rv-verdict';
  box.append(cause, sub, bar, q, grid, verdict);
  root.append(box);
  updateRevive(root, v);
}

/** 프레임마다 호출 — 바뀐 것만 쓴다. */
export function updateRevive(root: HTMLElement, v: ReviveView): void {
  const fill = root.querySelector<HTMLElement>('.rv-bar > i');
  if (fill !== null) {
    const pct = v.limitTicks > 0 ? Math.round((v.ticksLeft / v.limitTicks) * 100) : 0;
    const w = `${pct}%`;
    if (fill.style.width !== w) fill.style.width = w;
    fill.classList.toggle('low', v.ticksLeft <= 30);
  }
  const grid = root.querySelector('.rv-grid');
  if (grid !== null) {
    const locked = !v.armed || v.status !== 1;
    grid.classList.toggle('locked', locked);
    const buttons = grid.querySelectorAll('button');
    buttons.forEach((b, i) => {
      b.disabled = locked;
      b.classList.toggle('picked', i === v.picked);
      b.classList.toggle('reveal', i === v.revealIndex);
      b.classList.toggle('wrong', v.status === 3 && i === v.picked && i !== v.revealIndex);
    });
  }
  const verdict = root.querySelector('.rv-verdict');
  if (verdict !== null) {
    const text =
      v.status === 2
        ? t().revive.alive
        : v.status === 3
          ? v.picked < 0
            ? t().revive.timeout
            : t().revive.wrong
          : '';
    if (verdict.textContent !== text) verdict.textContent = text;
  }
}

export interface GameOverExtras {
  /** 대결 결과 한 줄(도전 판에서만) — 예: "도전 성공 · 상대 봉인 1:04" */
  readonly duelLine: string | null;
  /** '한 판 더' 버튼 문구 재정의(웹 대결 재도전이 남았으면 "같은 판 다시") */
  readonly restartLabel: string | null;
  readonly onShare: (() => Promise<void>) | null; // 결과 카드 공유
  readonly onCounter: (() => Promise<void>) | null; // 되받아치기(도전 판 · 웹)
  /** 웹 대결 시도 소진 — 앱 CTA. 주소(`VITE_APP_URL`)가 없으면 호출부가 null 을 넘겨 CTA 를 띄우지 않는다 */
  readonly appCta: { readonly label: string; readonly href: string } | null;
}

/**
 * 게임오버 패널 확장 — 버튼 순서는 고정이다: [한 판 더] 가 맨 위(oneMoreRate 가 UI 배치로 떨어지면
 * 재미 부족과 구분할 수 없다) → [결과 공유] → [되받아치기] → 앱 CTA. 공유 결과는 호출부가 토스트로 알린다.
 */
export function augmentGameOver(dom: DomRefs, x: GameOverExtras): void {
  const panel = dom.overlay.querySelector('.panel');
  if (panel === null) return;
  const restart = panel.querySelector<HTMLButtonElement>('#restart');
  if (restart !== null && x.restartLabel !== null) restart.textContent = x.restartLabel;
  if (x.duelLine !== null && restart !== null) {
    const d = document.createElement('p');
    d.className = 'duel-line';
    d.textContent = x.duelLine;
    panel.insertBefore(d, restart);
  }
  const actions = document.createElement('div');
  actions.className = 'go-actions';
  const mk = (label: string, cls: string, on: () => void): HTMLButtonElement => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = cls;
    b.textContent = label;
    b.addEventListener('click', on);
    return b;
  };
  if (x.onShare !== null) {
    const on = x.onShare;
    actions.append(mk(t().over.share, 'secondary', () => void on()));
  }
  if (x.onCounter !== null) {
    const on = x.onCounter;
    actions.append(mk(t().over.counter, 'secondary', () => void on()));
  }
  if (x.appCta !== null) {
    const a = document.createElement('a');
    a.className = 'app-cta';
    a.href = x.appCta.href;
    a.textContent = x.appCta.label;
    actions.append(a);
  }
  if (actions.childElementCount > 0) {
    if (restart !== null && restart.nextSibling !== null)
      panel.insertBefore(actions, restart.nextSibling);
    else panel.append(actions);
  }
  // 패널 안의 탭이 스테이지 재시작(pointerup)으로 새지 않게
  panel.addEventListener('pointerup', (e) => e.stopPropagation());
}

/** 공유 최종 종착지 — 항상 보이는 readonly textarea. body 전역 touch-action/user-select 를 여기서만 되돌린다. */
export function showManualShare(dom: DomRefs, text: string): void {
  const panel = dom.overlay.querySelector('.panel');
  if (panel === null) return;
  let box = panel.querySelector<HTMLTextAreaElement>('textarea.share-box');
  if (box === null) {
    box = document.createElement('textarea');
    box.className = 'share-box';
    box.readOnly = true;
    box.rows = 4;
    panel.append(box);
  }
  box.value = text;
  box.scrollIntoView({ block: 'center' });
  box.focus();
  box.select();
}
